// Ported verbatim from microsites/womens-conference/src/components/ShareInvite.tsx.
import { useState } from 'react';

/**
 * The flag-football lesson: a registered person's next action is not a
 * button to a page she is already on; it is bringing a friend. Hands over
 * the church site's event page (the pitch AND the signup), not this app.
 * navigator.share where it exists, clipboard where it does not.
 *
 * Only the link travels. Share sheets paste `text` and `url` together, so a
 * canned sentence turns a clean link into a paragraph someone has to edit
 * before sending; the event page already carries the pitch.
 */
export function ShareInvite({
  url,
  title,
  tone = 'primary',
}: {
  url: string;
  title: string;
  /**
   * Secondary where something outranks it. Once registration is open, a
   * registered woman's most likely next move is signing her sister up herself,
   * not sending her a link — so this steps back rather than competing with a
   * second identical marigold pill.
   */
  tone?: 'primary' | 'secondary';
}) {
  const [copied, setCopied] = useState(false);

  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        // Dismissed the share tray; nothing to do.
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copy this link', url);
    }
  }

  return (
    <button
      type="button"
      onClick={share}
      className={`wm-lift inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold ${
        tone === 'primary'
          ? 'bg-wm-marigold text-wm-sage-950 shadow-lg'
          : 'wm-glass-dark text-wm-cream ring-wm-cream/30 ring-1'
      }`}
    >
      {copied ? 'Link copied' : 'Invite a friend'}
    </button>
  );
}
